import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { ArrowLeft, Mail, KeyRound, CheckCircle } from "lucide-react";

const authSchema = z.object({
  email: z.string().trim().email({ message: "Inserisci un'email valida" }).max(255),
  password: z.string().min(6, { message: "La password deve essere di almeno 6 caratteri" }).max(100),
});

type ForgotStep = "email" | "emailSent" | "newPassword" | "notFound";

const Auth = () => {
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<ForgotStep>("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    checkUser();

    // Listen for password recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setShowForgotPassword(true);
        setForgotStep("newPassword");
      }
      if (event === "SIGNED_IN" && session) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validated = authSchema.parse({ email: loginEmail, password: loginPassword });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: validated.email,
        password: validated.password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Errore di accesso",
            description: "Email o password non corretti",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Errore",
            description: error.message,
            variant: "destructive",
          });
        }
      } else if (data.session) {
        toast({
          title: "Accesso effettuato!",
          description: "Benvenuto su Tecly",
        });
        navigate("/");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: "Dati non validi",
          description: err.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validated = authSchema.parse({ email: signupEmail, password: signupPassword });
      
      const { data, error } = await supabase.auth.signUp({
        email: validated.email,
        password: validated.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        if (error.message.includes("already registered")) {
          toast({
            title: "Account già esistente",
            description: "Questa email è già registrata. Prova ad accedere invece.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Errore",
            description: error.message,
            variant: "destructive",
          });
        }
      } else if (data.session) {
        toast({
          title: "Registrazione completata!",
          description: "Account creato con successo. Benvenuto su Tecly!",
        });
        navigate("/");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: "Dati non validi",
          description: err.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot password: send reset email with magic link
  const handleForgotSendEmail = async () => {
    if (!forgotEmail.trim()) {
      toast({ title: "Inserisci la tua email", variant: "destructive" });
      return;
    }

    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) {
        toast({
          title: "Errore",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email inviata!",
          description: "Controlla la tua casella email e clicca il link per reimpostare la password.",
        });
        setForgotStep("emailSent");
      }
    } catch (err) {
      toast({ title: "Errore", description: "Impossibile inviare l'email", variant: "destructive" });
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot password: set new password
  const handleSetNewPassword = async () => {
    if (newPassword.length < 6) {
      toast({ title: "Password troppo corta", description: "Minimo 6 caratteri", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Le password non coincidono", variant: "destructive" });
      return;
    }

    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        toast({ title: "Errore", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Password aggiornata!", description: "Puoi ora accedere con la nuova password." });
        setShowForgotPassword(false);
        setForgotStep("email");
        setForgotEmail("");
        
        setNewPassword("");
        setConfirmPassword("");
        navigate("/");
      }
    } catch (err) {
      toast({ title: "Errore", description: "Impossibile aggiornare la password", variant: "destructive" });
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep("email");
    setForgotEmail("");
    setNewPassword("");
    setConfirmPassword("");
  };

  // Forgot Password Flow
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000">
          <div className="text-center space-y-2">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              Tecly
            </h1>
            <p className="text-muted-foreground">Recupero Password</p>
          </div>

          <Card className="border-2 shadow-elegant">
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={resetForgotPassword}
                  className="p-2 rounded-full hover:bg-muted transition-all"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
                 <CardTitle className="text-xl flex-1 text-center pr-9">
                  {forgotStep === "email" && "Recupera Password"}
                  {forgotStep === "emailSent" && "Controlla la tua Email"}
                  {forgotStep === "newPassword" && "Nuova Password"}
                  {forgotStep === "notFound" && "Account Non Trovato"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {forgotStep === "email" && (
                <>
                  <CardDescription className="text-center">
                    Inserisci l'email del tuo account Tecly. Ti invieremo un codice di verifica.
                  </CardDescription>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="nome@esempio.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="pl-10 transition-all focus:shadow-md"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleForgotSendEmail}
                    disabled={forgotLoading || !forgotEmail.trim()}
                    className="w-full"
                  >
                    {forgotLoading ? "Invio in corso..." : "Invia Codice di Verifica"}
                  </Button>
                </>
              )}

              {forgotStep === "emailSent" && (
                <>
                  <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <CardDescription className="text-center">
                    Abbiamo inviato un link di recupero a <span className="font-medium text-foreground">{forgotEmail}</span>. Clicca il link nell'email per reimpostare la password.
                  </CardDescription>
                  <button
                    onClick={handleForgotSendEmail}
                    disabled={forgotLoading}
                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors text-center"
                  >
                    {forgotLoading ? "Invio in corso..." : "Non hai ricevuto l'email? Rinvia"}
                  </button>
                  <button
                    onClick={() => setForgotStep("email")}
                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors text-center"
                  >
                    Cambia email
                  </button>
                </>
              )}

              {forgotStep === "newPassword" && (
                <>
                  <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <CardDescription className="text-center">
                    Imposta la tua nuova password per l'account Tecly.
                  </CardDescription>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">Nuova Password</Label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Conferma Password</Label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={handleSetNewPassword}
                    disabled={forgotLoading || !newPassword || !confirmPassword}
                    className="w-full"
                  >
                    {forgotLoading ? "Aggiornamento..." : "Imposta Nuova Password"}
                  </Button>
                </>
              )}

              {forgotStep === "notFound" && (
                <>
                  <CardDescription className="text-center">
                    L'email <span className="font-medium text-foreground">{forgotEmail}</span> non è associata a nessun account Tecly.
                  </CardDescription>
                  <Button
                    onClick={() => {
                      resetForgotPassword();
                      // Pre-fill signup email
                      setSignupEmail(forgotEmail);
                    }}
                    className="w-full"
                  >
                    Registrati con questa email
                  </Button>
                  <button
                    onClick={() => setForgotStep("email")}
                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors text-center"
                  >
                    Prova con un'altra email
                  </button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000">
        {/* Logo/Title */}
        <div className="text-center space-y-2">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
            Tecly
          </h1>
          <p className="text-muted-foreground">Benvenuto sulla piattaforma</p>
        </div>

        {/* Auth Card */}
        <Card className="border-2 shadow-elegant">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl text-center">Inizia ora</CardTitle>
            <CardDescription className="text-center">
              Accedi o registrati per continuare
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  Accedi
                </TabsTrigger>
                <TabsTrigger value="signup" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  Registrati
                </TabsTrigger>
              </TabsList>

              {/* Login Form */}
              <TabsContent value="login" className="space-y-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="nome@esempio.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="transition-all focus:shadow-md"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="transition-all focus:shadow-md"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
                    disabled={loading}
                  >
                    {loading ? "Accesso in corso..." : "Accedi"}
                  </Button>
                  <div className="text-center">
                    <button 
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      Password dimenticata?
                    </button>
                  </div>
                </form>
              </TabsContent>

              {/* Signup Form */}
              <TabsContent value="signup" className="space-y-4">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="nome@esempio.com"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      className="transition-all focus:shadow-md"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      required
                      className="transition-all focus:shadow-md"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
                    disabled={loading}
                  >
                    {loading ? "Registrazione in corso..." : "Registrati"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
